import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import ScreenContext from '../../ScreenContext';
import NavActions from 'src/actions/NavActions';
import Events from 'src/logging/Events';
import {generateErrorMessage} from 'src/logging/generateErrorMessage';
import Styles from '../../Styles';
import KeyboardAvoidingView from '../../elements/365KeyboardAvoidingView';
import {StyleSheet, View, ScrollView} from 'react-native';
import MachineAction from 'src/actions/MachineActions';
import Localized from 'src/constants/AppStrings';
import {alertError} from '../../helpers/AlertHelper';
import FirebaseAnalytic from '../../../nativeModules/FirebaseAnalytic';
import CrashlyticsEvents from 'src/logging/Crashlytics';
import CustomTextInput from 'src/components/elements/CustomTextInput';
import AVText from 'src/components/elements/AVText';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';
import {isValidEmail} from 'src/services/ValidatorService';
import {Component} from 'react';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import BackSubheader from 'src/components/elements/BackSubheader';
import {subHeaderFontSize} from 'src/components/elements/layoutDescriptor/layout-redesign';

type ContactUsScreenProps = {
  popToTop: boolean;
  subject: string;
  insideTab: boolean;
  deviceId: string;
};
type ContactUsScreeState = {
  name: string;
  email: string;
  comment: string;
  mailError: string;
};

const {bodyLargeFontSize, bodyFontSize, mainFontFamily} = getLayoutDescriber();
const textColor = Styles.Colors.PayNew.black01;
const labelColor = '#707070';

class ContactUsScreen extends Component<
  ContactUsScreenProps,
  ContactUsScreeState
> {
  static defaultProps = {
    popToTop: false,
  };
  static contextType = ScreenContext;

  constructor(props: ContactUsScreenProps) {
    super(props);
    this.state = {
      name: '',
      email: '',
      comment: '',
      mailError: '',
    };
    this.cancelClicked = this.cancelClicked.bind(this);
    this.sendClicked = this.sendClicked.bind(this);
    this.sendEmail = this.sendEmail.bind(this);
    this.isValid = this.isValid.bind(this);
    this.validateEmail = this.validateEmail.bind(this);
  }

  cancelClicked() {
    FirebaseAnalytic.trackEvent('cancelClicked', 'ChooseContactScreen', {
      ...this.props,
      ...this.state,
    });
    if (this.props.popToTop) {
      NavActions.popToTop();
    } else {
      NavActions.pop();
    }
  }

  sendClicked() {
    FirebaseAnalytic.trackEvent('sendClicked', 'ChooseContactScreen', {
      ...this.props,
      ...this.state,
    });

    if (this.isValid()) {
      this.sendEmail();

      if (this.props.popToTop) {
        NavActions.popToTop();
      } else {
        NavActions.pop();
      }
    } else {
      alertError(Localized.Errors.email_required);
    }
  }

  async sendEmail() {
    FirebaseAnalytic.trackEvent('sendEmail', 'ChooseContactScreen', {
      ...this.props,
      ...this.state,
    });
    try {
      const deviceId = this.props.deviceId;
      MachineAction.sendEmail(
        deviceId,
        this.props.subject,
        this.state.name,
        this.state.email,
        this.state.comment,
      );
    } catch (error) {
      CrashlyticsEvents.log(
        'Exception',
        'ContactUsScreen:SendEmail',
        generateErrorMessage(error),
      );
      Events.Error.trackEvent(
        'Exception',
        'ContactUsScreen:SendEmail',
        generateErrorMessage(error),
      );
    }
  }

  isValid() {
    return this.state.email?.length > 0 && !this.state.mailError;
  }

  validateEmail() {
    if (isValidEmail(this.state.email)) {
      this.setState({
        mailError: undefined,
      });
    } else {
      this.setState({
        mailError: Localized.Errors.please_enter_the_valid_email,
      });
    }
  }

  render() {
    return (
      <BackSubheader
        title={Localized.Labels.report_problem}
        onBackSelect={this.cancelClicked}
      >
        <KeyboardAvoidingView
          style={styles.container}
          insideTab={this.props.insideTab}
        >
          <ScrollView>
            <AVText
              style={styles.title}
              testID="qa_fld_title"
              accessibilityLabel={Localized.Labels.report_form}
            >
              {Localized.Labels.report_form}
            </AVText>
            <AVText
              style={styles.instruction}
              testID="qa_fld_instruction"
              accessibilityLabel={Localized.Labels.report_form_instruction}
            >
              {Localized.Labels.report_form_instruction}
            </AVText>

            <CustomTextInput
              label={Localized.Labels.name_text}
              value={this.state.name}
              returnKeyType="next"
              accessible={true}
              accessibilityLabel={Localized.Labels.name_text}
              aria-label={Localized.Labels.name_text}
              onChangeText={(value) =>
                this.setState({
                  name: value,
                })
              }
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
              showShadow={true}
              textColor={textColor}
              fontSize={bodyFontSize()}
              labelStyle={[styles.emailLabel]}
              testID="qa_fld_Name"
            />
            <CustomTextInput
              label={Localized.Labels.email}
              value={this.state.email}
              autoCapitalize="none"
              keyboardType="email-address"
              onBlur={() => this.validateEmail()}
              returnKeyType="next"
              maxLength={75}
              accessible={true}
              accessibilityLabel={Localized.Labels.email}
              aria-label={Localized.Labels.email}
              onChangeText={(value) =>
                this.setState({
                  email: value,
                })
              }
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
              showShadow={true}
              textColor={textColor}
              fontSize={bodyFontSize()}
              hasErrorMsg={this.state.mailError ? true : false}
              errorMsg={this.state.mailError}
              errorTextStyle={styles.errorTextStyle}
              textStyle={styles.errorTextHeight}
              outlineStyle={
                this.state.mailError
                  ? {
                      borderColor: Styles.Colors.PayNew.error,
                      borderWidth: 1.5,
                    }
                  : {}
              }
              labelStyle={[styles.emailLabel]}
              testID="qa_fld_Email"
              errorMsgTestId="qa_emailError_txt"
            />
            <View accessible testID="qa_fld_Comment" nativeID="qa_fld_Comment">
              <CustomTextInput
                autoCapitalize="none"
                accessible={true}
                label={Localized.Labels.comment}
                accessibilityLabel={Localized.Labels.comment}
                accessibilityValue={{text: Localized.Labels.comment}}
                aria-label={Localized.Labels.comment}
                aria-valuetext={Localized.Labels.comment}
                value={this.state.comment}
                multiline={true}
                numberOfLines={5}
                onChangeText={(value) =>
                  this.setState({
                    comment: value,
                  })
                }
                maxLength={140}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
                returnKeyType="next"
                containerStyle={styles.commentContainer}
                contentStyle={styles.commentContent}
                clearButtonMode="while-editing"
                textColor={textColor}
                fontSize={bodyFontSize()}
                labelStyle={styles.commentLabel}
                showShadow={true}
              />
              <AVText style={styles.comment}>
                {this.state.comment.length + '/140'}
              </AVText>
            </View>
          </ScrollView>
          <RoundedAppButton
            onPress={this.sendClicked}
            buttonType={RoundedAppButtonType.Solid}
            accessibilityLabelValue={Localized.Buttons.send}
            isDisabled={!this.isValid()}
            buttonContainerStyle={styles.buttonContainer}
            buttonViewStyle={styles.buttonContainer}
            inActiveButtonViewStyle={styles.inActiveButtonViewStyle}
            titleText={Localized.Buttons.send}
          />
        </KeyboardAvoidingView>
      </BackSubheader>
    );
  }
}

const styles = StyleSheet.create({
  title: {
    fontSize: subHeaderFontSize(),
    fontFamily: mainFontFamily(),
    fontWeight: 'bold',
  },
  instruction: {
    fontSize: bodyFontSize(),
    fontFamily: mainFontFamily(),
    marginTop: Styles.Spacing.m3,
  },
  container: {
    flex: 1,
    marginHorizontal: Styles.Spacing.m3,
    paddingTop: Styles.Spacing.m3,
  },
  emailLabel: {
    fontSize: bodyLargeFontSize(),
    fontFamily: mainFontFamily(),
    fontWeight: '400',
    color: labelColor,
  },
  comment: {
    color: labelColor,
    fontSize: bodyFontSize(),
    alignSelf: 'flex-end',
    fontWeight: '400',
    fontFamily: mainFontFamily(),
    marginTop: Styles.Spacing.m1,
    marginRight: Styles.Spacing.m3,
  },
  commentLabel: {
    fontSize: bodyFontSize(),
    fontFamily: mainFontFamily(),
    fontWeight: '400',
    color: labelColor,
  },
  commentContent: {
    paddingBottom: Styles.Spacing.m1,
    paddingTop: Styles.Spacing.m2,
  },
  commentContainer: {
    maxHeight: 130,
    minHeight: 130,
  },
  buttonContainer: {
    alignSelf: 'flex-end',
    marginBottom: Styles.Spacing.m4,
    marginRight: Styles.Spacing.m2,
  },
  buttonViewStyle: {
    paddingHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
  },
  inActiveButtonViewStyle: {
    backgroundColor: Styles.Colors.PayNew.darkHuesBase04,
    paddingHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
  },
  errorTextStyle: {
    fontWeight: '400',
    fontFamily: mainFontFamily(),
    fontSize: bodyFontSize(),
    alignContent: 'flex-start',
    alignSelf: 'flex-start',
  },
  errorTextHeight: {
    height: 60,
  },
});
export default withForwardedNavigationParams<ContactUsScreenProps>()(
  ContactUsScreen,
);
